import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  LogOut,
  Loader2,
  ShieldCheck,
  Upload,
  CheckCircle2,
  Circle,
  FileDown,
} from "lucide-react";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";
import MsaDownloadButton from "@/components/MsaDownloadButton";

const LOGO_URL =
  "https://customer-assets.emergentagent.com/job_sriman-enterprise/artifacts/ng31c679_image.png";

const inputClass =
  "w-full bg-[#040812]/70 border border-white/10 focus:border-red-500/60 focus:outline-none text-white placeholder-slate-500 px-4 py-3 text-sm rounded-sm transition-colors";

const readFileAsDataURL = (file) =>
  new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(file);
  });

const VerifyBadge = ({ verified, label }) => (
  <span
    className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-sm border font-mono text-[10px] tracking-[0.14em] uppercase ${
      verified
        ? "bg-green-500/10 text-green-300 border-green-500/30"
        : "bg-slate-500/10 text-slate-400 border-slate-500/30"
    }`}
  >
    {verified ? <CheckCircle2 size={11} /> : <Circle size={11} />}
    {verified ? `${label} verified` : `${label} pending`}
  </span>
);

export default function CrmProfile() {
  const { user, logout, authHeader, apiBase } = useAuth();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [kyc, setKyc] = useState({ gst: "", pan: "" });
  const [gstFile, setGstFile] = useState(null);
  const [panFile, setPanFile] = useState(null);
  const [bank, setBank] = useState({
    holder: "",
    account_number: "",
    ifsc: "",
    bank_name: "",
    branch: "",
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${apiBase}/vendor/profile`, {
        headers: authHeader(),
      });
      setProfile(data);
      setName(data.name || "");
      setPhone(data.phone || "");
      setKyc({ gst: data.kyc?.gst || "", pan: data.kyc?.pan || "" });
      setBank({
        holder: data.bank?.holder || "",
        account_number: "",
        ifsc: data.bank?.ifsc || "",
        bank_name: data.bank?.bank_name || "",
        branch: data.bank?.branch || "",
      });
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err?.response?.data?.detail) || "Failed to load profile",
      );
    } finally {
      setLoading(false);
    }
  }, [apiBase, authHeader]);

  useEffect(() => {
    load();
  }, [load]);

  const handleLogout = async () => {
    await logout();
    navigate("/crm/login", { replace: true });
  };

  const saveProfile = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { name, phone };
      const kycPayload = { ...kyc };
      if (gstFile) kycPayload.gst_file = await readFileAsDataURL(gstFile);
      if (panFile) kycPayload.pan_file = await readFileAsDataURL(panFile);
      payload.kyc = kycPayload;

      const bankPayload = { ...bank };
      if (!bankPayload.account_number) delete bankPayload.account_number;
      payload.bank = bankPayload;

      const { data } = await axios.patch(`${apiBase}/vendor/profile`, payload, {
        headers: authHeader(),
      });
      setProfile(data);
      setGstFile(null);
      setPanFile(null);
      setBank((b) => ({ ...b, account_number: "" }));
      toast.success("Profile saved. Admin will re-verify.");
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err?.response?.data?.detail) || "Save failed",
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <main
      data-testid="crm-profile-page"
      className="min-h-screen bg-[#040812] pt-24 pb-24"
    >
      <div className="border-b border-white/5 bg-[#040812]/95 backdrop-blur-xl fixed top-0 inset-x-0 z-40">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
          <Link to="/crm" className="flex items-center gap-3">
            <span className="h-9 w-9">
              <img src={LOGO_URL} alt="Sriman Solutions" className="h-full w-full object-contain" />
            </span>
            <div className="flex flex-col leading-none">
              <span className="font-display text-white text-sm tracking-tight">Sriman Solutions</span>
              <span className="font-mono text-[9px] text-red-500 tracking-[0.28em] uppercase mt-1">Partner CRM</span>
            </div>
          </Link>
          <div className="flex items-center gap-3">
            <Link
              to="/crm"
              className="text-slate-400 hover:text-white text-xs font-mono tracking-widest uppercase"
            >
              Leads
            </Link>
            <MsaDownloadButton />
            <button
              onClick={handleLogout}
              className="inline-flex items-center gap-2 border border-white/15 hover:border-red-500 text-white text-xs font-medium px-4 py-2 rounded-sm transition-colors"
            >
              <LogOut size={14} /> Sign out
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6 lg:px-10 mt-6">
        <span className="overline">/CRM · Profile</span>
        <h1 className="mt-4 font-display text-white text-4xl md:text-5xl tracking-tighter font-light leading-tight">
          Your account
        </h1>
        <p className="mt-3 text-slate-500 text-sm">
          Keep your KYC and bank details up-to-date. We release payouts on the
          5th of every month to your verified account only.
        </p>

        {loading || !profile ? (
          <div className="mt-16 flex justify-center">
            <Loader2 size={22} className="animate-spin text-slate-500" />
          </div>
        ) : (
          <form onSubmit={saveProfile} data-testid="profile-form" className="mt-8 space-y-6">
            {/* Basic */}
            <div className="bg-[#0B132B]/50 border border-white/10 p-6 md:p-8">
              <div className="flex items-center justify-between">
                <div>
                  <div className="overline">Contact</div>
                  <h2 className="mt-2 font-display text-white text-xl tracking-tight">Basics</h2>
                </div>
                {profile.msa_reference && (
                  <span className="font-mono text-[10px] text-slate-500 tracking-widest">
                    MSA · {profile.msa_reference}
                  </span>
                )}
              </div>
              <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="overline mb-1.5 block">Name</label>
                  <input
                    data-testid="profile-name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className={inputClass}
                  />
                </div>
                <div>
                  <label className="overline mb-1.5 block">Email</label>
                  <input value={profile.email} disabled className={`${inputClass} opacity-70`} />
                </div>
                <div>
                  <label className="overline mb-1.5 block">Phone</label>
                  <input
                    data-testid="profile-phone"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    className={inputClass}
                  />
                </div>
                <div>
                  <label className="overline mb-1.5 block">Vendor type</label>
                  <input value={profile.vendor_type || ""} disabled className={`${inputClass} opacity-70 capitalize`} />
                </div>
              </div>
            </div>

            {/* KYC */}
            <div className="bg-[#0B132B]/50 border border-white/10 p-6 md:p-8">
              <div className="flex items-center justify-between">
                <div>
                  <div className="overline">Compliance</div>
                  <h2 className="mt-2 font-display text-white text-xl tracking-tight">KYC</h2>
                </div>
                <VerifyBadge verified={profile.kyc?.verified} label="KYC" />
              </div>
              <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="overline mb-1.5 block">GSTIN</label>
                  <input
                    data-testid="profile-gst"
                    value={kyc.gst}
                    onChange={(e) => setKyc((k) => ({ ...k, gst: e.target.value }))}
                    placeholder="22AAAAA0000A1Z5"
                    className={inputClass}
                  />
                </div>
                <div>
                  <label className="overline mb-1.5 block">PAN</label>
                  <input
                    data-testid="profile-pan"
                    value={kyc.pan}
                    onChange={(e) => setKyc((k) => ({ ...k, pan: e.target.value }))}
                    placeholder="AAAAA0000A"
                    className={inputClass}
                  />
                </div>
                <div>
                  <label className="overline mb-1.5 block">Upload GST proof</label>
                  <div className="flex items-center gap-3">
                    <label className={`${inputClass} cursor-pointer inline-flex items-center gap-2`}>
                      <Upload size={14} /> {gstFile ? gstFile.name : profile.kyc?.has_gst_file ? "Replace" : "Choose file"}
                      <input
                        type="file"
                        accept=".pdf,image/*"
                        onChange={(e) => setGstFile(e.target.files?.[0] || null)}
                        className="hidden"
                      />
                    </label>
                  </div>
                </div>
                <div>
                  <label className="overline mb-1.5 block">Upload PAN proof</label>
                  <div className="flex items-center gap-3">
                    <label className={`${inputClass} cursor-pointer inline-flex items-center gap-2`}>
                      <Upload size={14} /> {panFile ? panFile.name : profile.kyc?.has_pan_file ? "Replace" : "Choose file"}
                      <input
                        type="file"
                        accept=".pdf,image/*"
                        onChange={(e) => setPanFile(e.target.files?.[0] || null)}
                        className="hidden"
                      />
                    </label>
                  </div>
                </div>
              </div>
              <p className="text-xs text-slate-500 mt-4">Files up to 2MB. Changes reset verification — admin will re-verify.</p>
            </div>

            {/* Bank */}
            <div className="bg-[#0B132B]/50 border border-white/10 p-6 md:p-8">
              <div className="flex items-center justify-between">
                <div>
                  <div className="overline">Payouts</div>
                  <h2 className="mt-2 font-display text-white text-xl tracking-tight">Bank details</h2>
                </div>
                <VerifyBadge verified={profile.bank?.verified} label="Bank" />
              </div>
              <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="overline mb-1.5 block">Account holder</label>
                  <input
                    data-testid="profile-bank-holder"
                    value={bank.holder}
                    onChange={(e) => setBank((b) => ({ ...b, holder: e.target.value }))}
                    className={inputClass}
                  />
                </div>
                <div>
                  <label className="overline mb-1.5 block">
                    Account number {profile.bank?.account_number_last4 && (
                      <span className="text-slate-500 normal-case">
                        · on file ending {profile.bank.account_number_last4}
                      </span>
                    )}
                  </label>
                  <input
                    data-testid="profile-bank-acct"
                    type="text"
                    value={bank.account_number}
                    onChange={(e) => setBank((b) => ({ ...b, account_number: e.target.value }))}
                    placeholder={profile.bank?.account_number_last4 ? "Leave blank to keep existing" : ""}
                    className={inputClass}
                  />
                </div>
                <div>
                  <label className="overline mb-1.5 block">IFSC</label>
                  <input
                    data-testid="profile-bank-ifsc"
                    value={bank.ifsc}
                    onChange={(e) => setBank((b) => ({ ...b, ifsc: e.target.value }))}
                    className={inputClass}
                  />
                </div>
                <div>
                  <label className="overline mb-1.5 block">Bank name</label>
                  <input
                    data-testid="profile-bank-name"
                    value={bank.bank_name}
                    onChange={(e) => setBank((b) => ({ ...b, bank_name: e.target.value }))}
                    className={inputClass}
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="overline mb-1.5 block">Branch</label>
                  <input
                    value={bank.branch}
                    onChange={(e) => setBank((b) => ({ ...b, branch: e.target.value }))}
                    className={inputClass}
                  />
                </div>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <button
                type="submit"
                disabled={saving}
                data-testid="profile-save"
                className="inline-flex items-center gap-2 bg-red-500 hover:bg-red-600 disabled:opacity-70 text-white text-sm font-medium px-6 py-3 rounded-sm transition-colors"
              >
                {saving ? <Loader2 size={14} className="animate-spin" /> : <ShieldCheck size={14} />}
                Save profile
              </button>
              <span className="text-xs text-slate-500">
                Any edit to KYC or bank details will reset verification.
              </span>
            </div>
          </form>
        )}
      </div>
    </main>
  );
}
